import React, { FC, useEffect } from 'react';
import '@component/SendMessageRides/sendMessageRides.scss';
import SendMessageRidesSkeleton from '@uikit/Loader/SendMessageRidesSkeleton/SendMessageRidesSkeleton';
import { useNavigate, useParams } from 'react-router-dom';
import SendMessageRideCard from '@component/SendMessageRideCard/SendMessageRideCard';
import { LogoIcon } from '@src/svg';
import { useLazyGetMessageInfoQuery } from '@store/rtk/auth';
import { formatDate } from '@component/LightVersion/Reports/ReportsComponents/BarManyLinesChart';
import PageNotFound from '@component/PageNotFound/PageNotFound';

export const SendMessageRides: FC = () => {
  const navigate = useNavigate();
  const [getMessageInfo, { data: ride, isLoading, isError, error }] =
    useLazyGetMessageInfoQuery();

  const { id } = useParams();

  useEffect(() => {
    if (id) {
      getMessageInfo({ token: id });
    }
  }, [getMessageInfo, id]);

  useEffect(() => {
    const isExpired =
      error && 'originalStatus' in error && error.originalStatus === 410;

    if (isExpired) {
      navigate('/authorization/expired-link-sms', { replace: true });
    }
  }, [error, navigate]);

  const reloadRide = () => {
    if (id) {
      getMessageInfo({ token: id });
    }
  };

  if (isLoading) {
    return <SendMessageRidesSkeleton count={1} />;
  }

  if (isError) {
    return <PageNotFound />;
  }

  if (!ride) {
    return null;
  }

  const segments = ride.ridesSegments || [];
  const dos = ride.rideRequestDate;
  const confirmationCode = ride.rideOrderConfirmationCode;
  const memberName = ride.member?.fullName ?? 'Member';

  return (
    <div data-test-id="c-send-message-rides" className="send-message-rides">
      <div
        className="glob-header"
        data-test-id="c-send-message-rides-glob-header"
      >
        <LogoIcon />
      </div>
      <div
        className="send-message-rides__header"
        data-test-id="c-send-message-rides-header"
      >
        <div
          className="send-message-rides__title-block"
          data-test-id="c-send-message-rides-title-block"
        >
          <h2>
            {memberName}'s rides for {formatDate(dos)}
          </h2>
        </div>
        <button
          className="send-message-rides__code"
          data-test-id="c-send-message-rides-code"
        >
          {confirmationCode}
        </button>
      </div>

      <div
        className="send-message-rides__list"
        data-test-id="c-send-message-rides-list"
      >
        {segments.map((segment: any, idx: number) => (
          <SendMessageRideCard
            key={segment.id ?? idx}
            dataTestId={`c-send-message-rides-card-${idx}`}
            id={id as string}
            segment={segment}
            segments={segments}
            index={idx}
            onChanged={reloadRide}
          />
        ))}
      </div>
    </div>
  );
};
